// Functionality to edit the XML file in response to actions taken in the Preview, in inspector mode in the UI.
import { PropToUpdate } from '../../../types';

type GetModelCb = () => {
    getLineContent: (lineNumber: number) => string;
};

type PushEditOperations = (
    cursor: any[],
    operation: {
        range: {
            startLineNumber: number;
            endLineNumber: number;
            startColumn: number;
            endColumn: number;
        };
        text: string;
    }
) => void;

type AttrUpdates = {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    layoutPositioned?: number; // If >=1, then skip x/y updates
};

type IndexedLine = { line: number; index: number };

/**
 * Update XML attributes across a multi-line opening tag span.
 * - Scans lines [startLine .. startLine + (length-1)] for existing attributes.
 * - Replaces existing attrs on their line, or inserts missing attrs on the closing line.
 * - Emits edits only for changed lines.
 */
const updateXmlAttributesInSpan = (
    startLine: number,
    length: number,
    getModelCb: GetModelCb,
    pushEditOperationsCb: PushEditOperations,
    updates: AttrUpdates
): void => {
    const endLine: number = startLine + Math.max(0, (length ?? 1) - 1);
    const model: ReturnType<typeof getModelCb> = getModelCb();

    // Read lines in span into a map
    const lines: Record<number, string> = {};
    for (let line: number = startLine; line <= endLine; line++) lines[line] = model?.getLineContent(line) ?? '';

    // Regexes for attributes
    const xRe: RegExp = /\bx\s*=\s*["'][^"']*["']/;
    const yRe: RegExp = /\by\s*=\s*["'][^"']*["']/;
    const wRe: RegExp = /\bwidth\s*=\s*["'][^"']*["']/;
    const hRe: RegExp = /\bheight\s*=\s*["'][^"']*["']/;

    // Find the first line in span that matches regex
    const findAttrLine = (re: RegExp): number | null => {
        for (let line: number = startLine; line <= endLine; line++) if (re.test(lines[line])) return line;
        return null;
    };

    // Replace on specific line
    const replaceOnLine = (line: number, regExp: RegExp, replacement: string): void => {
        lines[line] = lines[line]?.replace(regExp, replacement);
    };

    const findInsertLocation = (): IndexedLine | null => {
        for (let line: number = startLine; line <= endLine; line++) {
            const text: string = lines[line] ?? '';
            let inQuote: string | null = null;
            for (let i: number = 0; i < text.length; i++) {
                const ch: string = text[i];
                if (ch === '"' || ch === "'") {
                    if (inQuote === ch) inQuote = null;
                    else if (!inQuote) inQuote = ch;
                } else if (ch === '>' && !inQuote) {
                    return { line, index: i };
                }
            }
        }
        return null;
    };

    const getLeadingWhitespace = (text: string): string => text.match(/^\s*/)?.[0] ?? '';

    const findAttributeIndent = (closingLine: number): string => {
        for (let line: number = closingLine - 1; line > startLine; line--) {
            const text: string = lines[line] ?? '';
            if (text.trim()) return getLeadingWhitespace(text);
        }

        const startIndent: string = getLeadingWhitespace(lines[startLine] ?? '');
        const closingIndent: string = getLeadingWhitespace(lines[closingLine] ?? '');
        const indentUnit: string = startIndent.includes('\t') || closingIndent.includes('\t') ? '\t' : '    ';
        const closingLineText: string = (lines[closingLine] ?? '').trim();
        return /^(\/?>)/.test(closingLineText) ? closingIndent + indentUnit : closingIndent;
    };

    // Insert before the closing '>' of the opening tag span
    const insertOnClosingLine = (attrTexts: string[]): number => {
        const location: IndexedLine | null = findInsertLocation();
        if (location && attrTexts.length > 0) {
            const { line, index } = location;
            const text: string = lines[line] ?? '';

            if (startLine === endLine) {
                let insertPos: number = index;

                // Walk backwards from the insertion index skipping whitespace to handle self-closing tags (`/>`)
                let j: number = insertPos - 1;
                while (j >= 0 && /\s/.test(text[j])) j--;
                if (j >= 0 && text[j] === '/') insertPos = j;

                lines[line] = text.slice(0, insertPos).trimEnd() + ` ${attrTexts.join(' ')}` + text.slice(insertPos);
                return line;
            }

            const attrIndent: string = findAttributeIndent(line);
            const attrBlock: string = attrTexts.map((attr: string) => `${attrIndent}${attr}`).join('\n');
            lines[line] = `${attrBlock}\n${text}`;
            return line;
        }

        // Fallback: append to the last line if no suitable location was found
        const fallbackLine: number = endLine;
        lines[fallbackLine] = (lines[fallbackLine] ?? '') + ` ${attrTexts.join(' ')}`;
        return fallbackLine;
    };

    const changed: Set<number> = new Set<number>();
    const attrsToInsert: string[] = [];

    // Width
    if (updates.width !== undefined) {
        const line: number | null = findAttrLine(wRe);
        if (line !== null) {
            replaceOnLine(line, wRe, `width="${updates.width}"`);
            changed.add(line);
        } else {
            attrsToInsert.push(`width="${updates.width}"`);
        }
    }

    // Height
    if (updates.height !== undefined) {
        const line: number | null = findAttrLine(hRe);
        if (line !== null) {
            replaceOnLine(line, hRe, `height="${updates.height}"`);
            changed.add(line);
        } else {
            attrsToInsert.push(`height="${updates.height}"`);
        }
    }

    // Skip if layout-positioned
    if ((updates.layoutPositioned ?? 0) < 1) {
        // X
        if (updates.x !== undefined) {
            const line: number | null = findAttrLine(xRe);
            if (line !== null) {
                replaceOnLine(line, xRe, `x="${updates.x}"`);
                changed.add(line);
            } else {
                attrsToInsert.push(`x="${updates.x}"`);
            }
        }
        // Y
        if (updates.y !== undefined) {
            const line: number | null = findAttrLine(yRe);
            if (line !== null) {
                replaceOnLine(line, yRe, `y="${updates.y}"`);
                changed.add(line);
            } else {
                attrsToInsert.push(`y="${updates.y}"`);
            }
        }
    }

    if (attrsToInsert.length > 0) changed.add(insertOnClosingLine(attrsToInsert));

    // Emit edits for changed lines only
    for (const line of changed) {
        const original: string = model.getLineContent(line) ?? '';
        const updated: string = lines[line];
        if (updated !== original) {
            pushEditOperationsCb([], {
                range: {
                    startLineNumber: line,
                    endLineNumber: line,
                    startColumn: 1,
                    endColumn: original.length + 1
                },
                text: updated
            });
        }
    }
};

export const updateEditorProp = (
    propToUpdate: PropToUpdate,
    getModelCb: GetModelCb,
    pushEditOperationsCb: PushEditOperations
) => {
    const { lineNumber, multiLineLength, deltaX, deltaY, width, height, dragStartPos, layoutPositioned } = propToUpdate;
    if (lineNumber === 0) return;

    const model: ReturnType<typeof getModelCb> = getModelCb();
    const start: number = lineNumber;
    const end: number = start + (propToUpdate?.multiLineLength ?? 1) - 1;

    // Updated regex to match any value (not just integers)
    const xRe: RegExp = /\bx\s*=\s*["']([^"']*)["']/;
    const yRe: RegExp = /\by\s*=\s*["']([^"']*)["']/;
    let currentX: number = 0;
    let currentY: number = 0;

    for (let line = start; line <= end; line++) {
        const text: string = model?.getLineContent(line) || '';
        const xMatch: RegExpMatchArray | null = text.match(xRe);
        const yMatch: RegExpMatchArray | null = text.match(yRe);

        // Try to parse as number, default to 0 if not a number
        if (xMatch) {
            const xValue: string = xMatch[1];
            const parsed: number = parseFloat(xValue);
            currentX = isNaN(parsed) ? 0 : parsed;
        }
        if (yMatch) {
            const yValue: string = yMatch[1];
            const parsed: number = parseFloat(yValue);
            currentY = isNaN(parsed) ? 0 : parsed;
        }
    }

    let nextX: number | undefined;
    let nextY: number | undefined;
    if (dragStartPos) {
        // When dragging, always use the drag start position + delta (overrides any existing value)
        nextX = dragStartPos.x + (deltaX ?? 0);
        nextY = dragStartPos.y + (deltaY ?? 0);
    } else {
        nextX = deltaX !== undefined ? currentX + deltaX : undefined;
        nextY = deltaY !== undefined ? currentY + deltaY : undefined;
    }

    updateXmlAttributesInSpan(lineNumber, multiLineLength || 1, getModelCb, pushEditOperationsCb, {
        x: nextX,
        y: nextY,
        width,
        height,
        layoutPositioned
    });
};
